import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { UamErrorText, UamErrorTextContainer, UamRegistrationPanelContainer } from '../../../../styles-and-themes/styles/uam-registration-styles';
import { setFieldAndValidate } from '../../../../utils/field-utils';
import EmailControlSet from './ControlSets/EmailControlSet/EmailControlSet';
import NextButtonControlSet from './ControlSets/NextButtonControlSet/NextButtonControlSet';
import { userFieldSchemas } from '../../../../utils/validation-utils';
import { composeDestinationQuerystring } from '../../../../../shared-with-server/destination-utils';
import { destinationInfo } from '../../../../destructured-front-end-config';
import { getLocalStorageRegistrationInfo, multiStepResultTypes, processMultiStepRegistrationEntry, setLocalStorageRegistrationInfo } from '../../../../utils/uam-utils';

const RegistrationEmailPanel = ({ destPath, email, setEmail, setEmailPending, setRegistrationComplete }) => {
  const [liveEmailStatus, setLiveEmailStatus] = useState('');

  const isFormValid = (email.state === 2 && !email.error?.length && !liveEmailStatus?.length);

  useEffect(() => {
    const registrationEmail = getLocalStorageRegistrationInfo('email');
    if (registrationEmail?.length) {
      setFieldAndValidate(email, setEmail, registrationEmail, userFieldSchemas.email);
    }
  }, []);

  useEffect(
    () => {
      if (email.state === 2) {
        axios.post(
          '/api/v2/fi/validators/email',
          {
            email: email.value,
          },
        ).then(
          (response) => {
            setLiveEmailStatus(
              response.status === 200 && response.data.restrictions.includes('front-end-email-reg')
                ? 'Your institution does not allow independent accounts. For access to this site, please contact your institution\'s Achieve administrator.'
                : '',
            );
          },
        ).catch(
          () => {
            setLiveEmailStatus('');
          },
        );
      }
    },
    [email],
  );

  const onNext = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isFormValid) {
      return;
    }

    axios.post(
      `/api/v2/fi/multi-step-registration-entry?${composeDestinationQuerystring(destinationInfo, destPath)}`,
      { email: email.value.trim().toLowerCase() },
      { validateStatus: (status) => status < 500 }
    ).then((res) => {
      const result = processMultiStepRegistrationEntry(res.status, res.data, setEmailPending, setRegistrationComplete);

      if (result.status === multiStepResultTypes.error) {
        alert('An unexpected error occurred while saving registration data. You will need to start over.');
        console.error('Error saving registration data', res);
        window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
      }

      const priorEmail = getLocalStorageRegistrationInfo('email');
      if (priorEmail?.length && priorEmail !== email.value.trim().toLowerCase()) {
        localStorage.clear('registration_info');
      }

      setLocalStorageRegistrationInfo('email', email.value.trim().toLowerCase());

      if (result.status === multiStepResultTypes.new_registration) {
        localStorage.setItem('is_new_registration', 'true');
      }

      const { step } = result;
      const stepNum = Number(step);
      if (!isNaN(stepNum)) {
        window.location.assign(`/uam/step${stepNum}?${composeDestinationQuerystring(destinationInfo, destPath)}`);
      }
    });
  }

  return (
    <form onSubmit={onNext}>
      <UamRegistrationPanelContainer>
        <UamErrorTextContainer><UamErrorText>All fields are required</UamErrorText></UamErrorTextContainer>

        <EmailControlSet
          email={email}
          setEmail={setEmail}
          liveEmailStatus={liveEmailStatus}
        />

        <NextButtonControlSet
          onNext={onNext}
          isFormValid={isFormValid}
        />
      </UamRegistrationPanelContainer>
    </form>
  )
};

RegistrationEmailPanel.propTypes = {
  destPath: PropTypes.object.isRequired,
  setEmailPending: PropTypes.func.isRequired,
  setRegistrationComplete: PropTypes.func.isRequired,
};

export default RegistrationEmailPanel;